import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { ROLES } from '@/lib/roles';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  UserCircle,
  Mail,
  Phone,
  AlertTriangle,
  Download,
  FileDown,
  FileText,
  FileUp,
  Upload,
  UserPlus,
  Check,
  Copy
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'registration_form', label: 'Inschrijfformulier' },
  { value: 'privacy_consent', label: 'Privacy/AVG-toestemming' },
  { value: 'photo_video_consent', label: 'Foto-/videotoestemming' },
  { value: 'volunteer_declaration', label: 'Vrijwilligersverklaring' },
  { value: 'vog', label: 'VOG' },
  { value: 'baptism_membership', label: 'Doop-/lidmaatschapsdocument' },
  { value: 'other', label: 'Overig' },
];

const MEMBER_STATUSES = [
  { value: 'active', label: 'Actief' },
  { value: 'inactive', label: 'Inactief' },
  { value: 'departed', label: 'Uitgeschreven' },
];

const getOptionLabel = (options, value) => options.find(option => option.value === value)?.label || value;

const getDocumentTypeLabel = (value) => DOCUMENT_TYPES.find(type => type.value === value)?.label || value;

const MembersPage = () => {
  const { hasCapability, isAdmin } = useAuth();
  const canManageMembers = hasCapability('members_manage');
  const canAddBasicMember = hasCapability('members_add_basic');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChildren, setShowChildren] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [householdCandidates, setHouseholdCandidates] = useState([]);
  const [addHouseholdMemberId, setAddHouseholdMemberId] = useState('');
  const [addHouseholdRole, setAddHouseholdRole] = useState('ouder_voogd');
  const [inviteRoles, setInviteRoles] = useState([]);
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedInviteLink, setCopiedInviteLink] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    document_type: 'registration_form',
    expires_at: '',
    note: '',
    file: null
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'active',
    member_number: '',
    date_of_birth: '',
    membership_since: '',
    address: '',
    postal_code: '',
    city: '',
    notes: '',
  });

  useEffect(() => {
    fetchMembers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchMembers(searchQuery);
  }, [showChildren]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async (search = '') => {
    try {
      const params = { include_children: showChildren };
      if (search) params.search = search;
      const response = await axios.get(`${API}/members`, { params });
      setMembers(response.data);
    } catch (error) {
      toast.error('Kon leden niet laden');
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (data, filename, type = 'text/csv;charset=utf-8') => {
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/members/export`, { responseType: 'blob' });
      downloadBlob(response.data, `leden-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Ledenbestand geëxporteerd');
    } catch (error) {
      toast.error('Exporteren mislukt');
    }
  };

  const runImport = async (dryRun) => {
    if (!importFile) {
      toast.error('Kies eerst een CSV-bestand');
      return;
    }
    const payload = new FormData();
    payload.append('file', importFile);
    payload.append('dry_run', String(dryRun));
    setImportLoading(true);
    try {
      const response = await axios.post(`${API}/members/import`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (!dryRun && response.data.imported > 0) {
        toast.success(`${response.data.imported} leden geïmporteerd`);
        setImportDialogOpen(false);
        setImportFile(null);
        setImportResult(null);
        fetchMembers(searchQuery);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV verwerken mislukt');
    } finally {
      setImportLoading(false);
    }
  };

  const fetchDocuments = async (memberId) => {
    if (!canManageMembers || !memberId) return;
    setDocumentsLoading(true);
    try {
      const response = await axios.get(`${API}/members/${memberId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Kon documenten niet laden');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchMembers(query);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Naam is verplicht');
      return;
    }

    const payload = {
      ...formData,
      email: formData.email || null,
      phone: formData.phone || null,
      member_number: formData.member_number || null,
      date_of_birth: formData.date_of_birth || null,
      membership_since: formData.membership_since || null,
      address: formData.address || null,
      postal_code: formData.postal_code || null,
      city: formData.city || null,
      notes: formData.notes || null,
    };

    try {
      if (editingMember) {
        await axios.put(`${API}/members/${editingMember.id}`, payload);
        toast.success('Lid bijgewerkt');
      } else {
        await axios.post(`${API}/members`, payload);
        toast.success('Lid toegevoegd');
      }

      setDialogOpen(false);
      resetForm();
      fetchMembers(searchQuery);
    } catch (error) {
      console.error('Member error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit lid wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/members/${id}`);
      toast.success('Lid verwijderd');
      fetchMembers(searchQuery);
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      status: member.status || 'active',
      member_number: member.member_number || '',
      date_of_birth: member.date_of_birth || '',
      membership_since: member.membership_since || '',
      address: member.address || '',
      postal_code: member.postal_code || '',
      city: member.city || '',
      notes: member.notes || '',
    });
    setDocuments([]);
    setDocumentForm({
      document_type: 'registration_form',
      expires_at: '',
      note: '',
      file: null
    });
    if (canManageMembers) {
      fetchDocuments(member.id);
    }
    setAddHouseholdMemberId('');
    setAddHouseholdRole('ouder_voogd');
    if (canManageMembers || canAddBasicMember) {
      fetchHousehold(member);
    }
    setInviteRoles([]);
    setInviteResult(null);
    setDialogOpen(true);
  };

  const toggleInviteRole = (roleValue) => {
    setInviteRoles((current) => (
      current.includes(roleValue) ? current.filter((r) => r !== roleValue) : [...current, roleValue]
    ));
  };

  const submitInvite = async () => {
    if (!editingMember?.email) { toast.error('Dit lid heeft geen e-mailadres'); return; }
    if (!inviteRoles.length) { toast.error('Kies minstens één rol'); return; }
    try {
      const response = await axios.post(`${API}/team/invite`, null, {
        params: { name: editingMember.name, email: editingMember.email, roles: inviteRoles },
      });
      setInviteResult(response.data);
      toast.success('Uitnodiging verstuurd');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
    }
  };

  const copyInviteLink = () => {
    if (!inviteResult?.invite_link) return;
    navigator.clipboard.writeText(inviteResult.invite_link);
    setCopiedInviteLink(true);
    setTimeout(() => setCopiedInviteLink(false), 2000);
  };

  const fetchHousehold = async (member) => {
    try {
      const response = await axios.get(`${API}/members`, { params: { include_children: true } });
      const all = response.data;
      setHouseholdCandidates(all.filter((m) => m.id !== member.id));
      setHouseholdMembers(member.household_id ? all.filter((m) => m.household_id === member.household_id && m.id !== member.id) : []);
    } catch (error) {
      toast.error('Kon huishouden niet laden');
    }
  };

  const submitAddToHousehold = async () => {
    if (!addHouseholdMemberId) return;
    try {
      // editingMember is the anchor: its existing household (if any) is reused/extended,
      // never the other way round, so we never accidentally drop editingMember's own household.
      await axios.post(`${API}/members/${addHouseholdMemberId}/household`, {
        parent_member_id: editingMember.id,
        role: addHouseholdRole,
      });
      toast.success('Toegevoegd aan het huishouden');
      setAddHouseholdMemberId('');
      await fetchHousehold(editingMember);
      fetchMembers(searchQuery);
    } catch (error) {
      toast.error('Kon niet toevoegen aan huishouden');
    }
  };

  const resetForm = () => {
    setEditingMember(null);
    setDocuments([]);
    setDocumentForm({
      document_type: 'registration_form',
      expires_at: '',
      note: '',
      file: null
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      status: 'active',
      member_number: '',
      date_of_birth: '',
      membership_since: '',
      address: '',
      postal_code: '',
      city: '',
      notes: '',
    });
  };

  const handleDocumentUpload = async () => {
    if (!editingMember || !documentForm.file) {
      toast.error('Kies eerst een document');
      return;
    }

    const payload = new FormData();
    payload.append('file', documentForm.file);
    payload.append('document_type', documentForm.document_type);
    if (documentForm.expires_at) payload.append('expires_at', documentForm.expires_at);
    if (documentForm.note) payload.append('note', documentForm.note);

    setUploadingDocument(true);
    try {
      await axios.post(`${API}/members/${editingMember.id}/documents`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document geüpload');
      setDocumentForm({
        document_type: 'registration_form',
        expires_at: '',
        note: '',
        file: null
      });
      const fileInput = document.getElementById('person-document-file');
      if (fileInput) fileInput.value = '';
      fetchDocuments(editingMember.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload mislukt');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDocumentDownload = async (documentItem) => {
    if (!editingMember) return;
    try {
      const response = await axios.get(
        `${API}/members/${editingMember.id}/documents/${documentItem.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: documentItem.content_type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = documentItem.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download mislukt');
    }
  };

  const handleDocumentDelete = async (documentId) => {
    if (!editingMember) return;
    if (!window.confirm('Weet je zeker dat je dit document wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/members/${editingMember.id}/documents/${documentId}`);
      toast.success('Document verwijderd');
      fetchDocuments(editingMember.id);
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-2xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="members-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Ledenbestand</h1>
            <p className="text-slate-500">Beheer gemeenteleden met een stabiel lidnummer</p>
          </div>
          {(canManageMembers || canAddBasicMember) && (
            <div className="flex flex-wrap gap-2">
              {canManageMembers && (
                <>
                  <Button type="button" variant="outline" onClick={handleExport} className="rounded-xl">
                    <FileDown className="w-4 h-4 mr-2" />
                    CSV exporteren
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setImportFile(null);
                      setImportResult(null);
                      setImportDialogOpen(true);
                    }}
                    className="rounded-xl"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    CSV importeren
                  </Button>
                </>
              )}
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                data-testid="add-member-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Lid toevoegen
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Zoek op lidnummer, naam, e-mail of telefoon..."
            className="pl-10 h-11 rounded-xl border-slate-200"
            data-testid="member-search"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showChildren}
            onChange={(e) => setShowChildren(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
          />
          Toon kinderen (&lt; 12 jaar)
        </label>

        {/* Members Table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {members.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-semibold">Lid</TableHead>
                    <TableHead className="font-semibold">Contact</TableHead>
                    <TableHead className="font-semibold">Lidnummer</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Toegevoegd</TableHead>
                    {canManageMembers && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-medium">
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {member.name}
                              {member.household_role === 'kind' && (
                                <Badge variant="secondary" className="bg-amber-50 text-amber-700 text-[10px]">Kind</Badge>
                              )}
                            </div>
                            {member.notes && (
                              <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                {member.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {member.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail className="w-3 h-3" />
                              {member.email}
                            </div>
                          )}
                          {member.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Phone className="w-3 h-3" />
                              {member.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <span className="font-mono text-xs">{member.member_number}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={member.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {getOptionLabel(MEMBER_STATUSES, member.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {member.created_at && format(
                          typeof member.created_at === 'string' 
                            ? parseISO(member.created_at) 
                            : new Date(member.created_at),
                          'd MMM yyyy',
                          { locale: nl }
                        )}
                      </TableCell>
                      {canManageMembers && <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(member)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(member.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-12 text-center">
                <UserCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? 'Geen resultaten' : 'Geen gemeenteleden'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {searchQuery 
                    ? 'Probeer een andere zoekopdracht'
                    : 'Voeg je eerste lid toe om te beginnen'}
                </p>
                {!searchQuery && (canManageMembers || canAddBasicMember) && (
                  <Button 
                    onClick={() => setDialogOpen(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Eerste lid toevoegen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMember ? 'Gemeentelid bewerken' : 'Gemeentelid toevoegen'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Naam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Volledige naam"
                  className="rounded-xl"
                  data-testid="member-name-input"
                />
              </div>

              {!canManageMembers && (
                <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                  Je kunt een nieuw lid toevoegen met naam en e-mailadres. Overige gegevens worden door het secretariaat aangevuld.
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="naam@voorbeeld.nl" className="rounded-xl" />
              </div>

              {canManageMembers && (
                <>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MEMBER_STATUSES.map(status => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefoon</Label>
                      <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+31 6 12345678" className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="member_number">Lidnummer</Label>
                      <Input id="member_number" value={formData.member_number} onChange={(e) => setFormData({ ...formData, member_number: e.target.value })} placeholder="Automatisch bij leeg veld" className="rounded-xl font-mono" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Geboortedatum</Label>
                      <Input id="date_of_birth" type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="membership_since">Lid sinds</Label>
                      <Input id="membership_since" type="date" value={formData.membership_since} onChange={(e) => setFormData({ ...formData, membership_since: e.target.value })} className="rounded-xl" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Adres</Label>
                    <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Straat en huisnummer" className="rounded-xl" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postcode</Label>
                      <Input id="postal_code" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Plaats</Label>
                      <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="rounded-xl" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notities</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Optionele administratieve notities..."
                      className="rounded-xl resize-none"
                      rows={3}
                    />
                  </div>
                </>
              )}

              {editingMember && (canManageMembers || canAddBasicMember) && (
                <div className="border-t border-slate-100 pt-5 space-y-3">
                  <h3 className="font-semibold text-slate-900">Huishouden</h3>
                  {householdMembers.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {householdMembers.map((m) => (
                        <span key={m.id} className="text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                          {m.name}{m.household_role ? ` (${m.household_role === 'ouder_voogd' ? 'ouder/voogd' : m.household_role === 'kind' ? 'kind' : 'overig'})` : ''}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Nog geen huishouden gekoppeld.</p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={addHouseholdMemberId || 'none'} onValueChange={(v) => setAddHouseholdMemberId(v === 'none' ? '' : v)}>
                      <SelectTrigger className="rounded-xl flex-1"><SelectValue placeholder="Voeg bestaand lid toe" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Kies een lid</SelectItem>
                        {householdCandidates.filter((m) => !householdMembers.some((h) => h.id === m.id)).map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={addHouseholdRole} onValueChange={setAddHouseholdRole}>
                      <SelectTrigger className="rounded-xl sm:w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ouder_voogd">Ouder/voogd</SelectItem>
                        <SelectItem value="kind">Kind</SelectItem>
                        <SelectItem value="overig">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={submitAddToHousehold} disabled={!addHouseholdMemberId} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl shrink-0">
                      Toevoegen
                    </Button>
                  </div>
                </div>
              )}

              {editingMember && isAdmin && (
                <div className="border-t border-slate-100 pt-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-slate-500" />
                    <h3 className="font-semibold text-slate-900">Uitnodigen als teamlid</h3>
                  </div>
                  {inviteResult ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-emerald-50 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
                        <Check className="w-4 h-4 shrink-0" />
                        {inviteResult.user.name} is uitgenodigd met de gekozen rol(len).
                      </div>
                      <div className="p-3 bg-amber-50 rounded-xl">
                        <p className="text-xs font-medium text-amber-800 mb-2">Uitnodigingslink (reserve, als de mail niet aankomt):</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-2 bg-white rounded text-amber-900 font-mono text-xs break-all">{inviteResult.invite_link}</code>
                          <Button type="button" size="sm" variant="outline" onClick={copyInviteLink} className="shrink-0">
                            {copiedInviteLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : !editingMember.email ? (
                    <p className="text-sm text-slate-500">Voeg eerst een e-mailadres toe om dit lid uit te nodigen.</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {ROLES.map((role) => (
                          <button
                            type="button"
                            key={role.value}
                            onClick={() => toggleInviteRole(role.value)}
                            className={`text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
                              inviteRoles.includes(role.value)
                                ? `${role.color} border-transparent`
                                : 'bg-white text-slate-500 border-slate-200'
                            }`}
                          >
                            {role.label}
                          </button>
                        ))}
                      </div>
                      <Button type="button" onClick={submitInvite} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                        Uitnodiging versturen naar {editingMember.email}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {editingMember && canManageMembers && (
                <div className="border-t border-slate-100 pt-5 space-y-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-slate-500" />
                      <h3 className="font-semibold text-slate-900">Documenten</h3>
                    </div>
                    <div className="mt-3 rounded-xl bg-amber-50 text-amber-800 p-3 text-sm flex gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>
                        Gebruik dit voor formulieren en toestemmingsdocumenten. Gevoelige pastorale informatie is niet bedoeld voor deze eerste versie.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Documenttype</Label>
                        <Select
                          value={documentForm.document_type}
                          onValueChange={(value) => setDocumentForm({ ...documentForm, document_type: value })}
                        >
                          <SelectTrigger className="rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DOCUMENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="document_expires_at">Vervaldatum optioneel</Label>
                        <Input
                          id="document_expires_at"
                          type="date"
                          value={documentForm.expires_at}
                          onChange={(e) => setDocumentForm({ ...documentForm, expires_at: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="person-document-file">Bestand</Label>
                      <Input
                        id="person-document-file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files?.[0] || null })}
                        className="rounded-xl"
                      />
                      <p className="text-xs text-slate-500">Toegestaan: PDF, JPG, PNG, DOC en DOCX. Maximaal 10 MB.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="document_note">Notitie optioneel</Label>
                      <Textarea
                        id="document_note"
                        value={documentForm.note}
                        onChange={(e) => setDocumentForm({ ...documentForm, note: e.target.value })}
                        placeholder="Korte administratieve notitie..."
                        className="rounded-xl resize-none"
                        rows={2}
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handleDocumentUpload}
                      disabled={uploadingDocument}
                      className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingDocument ? 'Uploaden...' : 'Document uploaden'}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {documentsLoading ? (
                      <div className="text-sm text-slate-500">Documenten laden...</div>
                    ) : documents.length > 0 ? (
                      <div className="rounded-xl border border-slate-200 overflow-hidden">
                        {documents.map((documentItem) => (
                          <div
                            key={documentItem.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border-b border-slate-100 last:border-b-0"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{documentItem.original_filename}</p>
                              <p className="text-xs text-slate-500">
                                {getDocumentTypeLabel(documentItem.document_type)} · geüpload door {documentItem.uploaded_by_name || 'Onbekend'} · {' '}
                                {documentItem.uploaded_at && format(
                                  typeof documentItem.uploaded_at === 'string'
                                    ? parseISO(documentItem.uploaded_at)
                                    : new Date(documentItem.uploaded_at),
                                  'd MMM yyyy',
                                  { locale: nl }
                                )}
                                {documentItem.expires_at ? ` · verloopt ${documentItem.expires_at}` : ''}
                              </p>
                              {documentItem.note && (
                                <p className="text-xs text-slate-600 mt-1 line-clamp-2">{documentItem.note}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="rounded-xl"
                                onClick={() => handleDocumentDownload(documentItem)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDocumentDelete(documentItem.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                        Nog geen documenten gekoppeld aan deze persoon.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="rounded-xl"
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  data-testid="save-member-btn"
                >
                  {editingMember ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gemeenteleden importeren</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600 space-y-1">
                <p>Gebruik een CSV met minimaal de kolom <strong>name</strong> of <strong>naam</strong>.</p>
                <p>Ondersteunde kolommen: memberNumber, e-mail, telefoon, status, geboortedatum, lid sinds, adres, postcode, plaats en notities.</p>
                <p>Een ontbrekend lidnummer wordt automatisch gegenereerd. E-mail wordt nooit als identificatie gebruikt.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="member-csv-file">CSV-bestand</Label>
                <Input
                  id="member-csv-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => {
                    setImportFile(event.target.files?.[0] || null);
                    setImportResult(null);
                  }}
                  className="rounded-xl"
                />
                <p className="text-xs text-slate-500">Maximaal 5 MB en 5.000 regels. Komma en puntkomma worden ondersteund.</p>
              </div>

              <Button type="button" variant="outline" onClick={() => runImport(true)} disabled={!importFile || importLoading} className="rounded-xl">
                {importLoading ? 'Controleren...' : 'CSV controleren'}
              </Button>

              {importResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">Regels</p><p className="font-semibold">{importResult.total_rows}</p></div>
                    <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs text-emerald-700">Geldig</p><p className="font-semibold text-emerald-800">{importResult.valid_rows}</p></div>
                    <div className="rounded-xl bg-red-50 p-3"><p className="text-xs text-red-700">Fouten</p><p className="font-semibold text-red-800">{importResult.errors?.length || 0}</p></div>
                    <div className="rounded-xl bg-amber-50 p-3"><p className="text-xs text-amber-700">Waarschuwingen</p><p className="font-semibold text-amber-800">{importResult.warnings?.length || 0}</p></div>
                  </div>

                  {importResult.errors?.length > 0 && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      <p className="font-semibold mb-2">Los deze fouten eerst op:</p>
                      {importResult.errors.slice(0, 20).map((error, index) => (
                        <p key={`${error.row}-${index}`}>Regel {error.row}: {error.message}</p>
                      ))}
                    </div>
                  )}

                  {importResult.warnings?.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      {importResult.warnings.slice(0, 20).map((warning, index) => (
                        <p key={`${warning.row}-${index}`}>Regel {warning.row}: {warning.message}</p>
                      ))}
                    </div>
                  )}

                  {importResult.preview?.length > 0 && (
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Regel</TableHead>
                            <TableHead>Lidnummer</TableHead>
                            <TableHead>Naam</TableHead>
                            <TableHead>E-mail</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importResult.preview.slice(0, 10).map(row => (
                            <TableRow key={row.row}>
                              <TableCell>{row.row}</TableCell>
                              <TableCell className="font-mono text-xs">{row.memberNumber}</TableCell>
                              <TableCell>{row.name}</TableCell>
                              <TableCell>{row.email || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)} className="rounded-xl">Sluiten</Button>
              <Button
                type="button"
                onClick={() => runImport(false)}
                disabled={!importResult?.can_import || importLoading}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                <FileUp className="w-4 h-4 mr-2" />
                {importLoading ? 'Importeren...' : `${importResult?.valid_rows || 0} leden importeren`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MembersPage;
